<template>
    <div class="map-container" role="region" :aria-label="t('map_description')">
        <div ref="mapContainer" class="map"></div>
        <button type="button" class="recenter-button button" @click="recenterMap('route')">
            {{ t('recenter') }}
        </button>
        <button type="button" class="geo-button" @click="locateUser">
            <span class="visuallyhidden">{{ t('user_position') }}</span>
            <IconGeolocation width="24" height="24" />
        </button>
    </div>
</template>

<script setup>
import 'maplibre-gl/dist/maplibre-gl.css';

import { useI18n } from 'vue-i18n';
import { ref, watch, onMounted, nextTick } from 'vue';
import { toGeoJSON } from 'geojson-polyline';
import maplibregl from 'maplibre-gl';
import GeoJSON from 'geojson';

import { useMap } from '@/composables/useMap';
import { useSchedulesStore } from '@@/js/stores/schedules';

import IconGeolocation from '@@/icons/geolocation.svg';

const props = defineProps({
    route: {
        type: Object,
        default: () => ({}),
    },
    direction: {
        type: Object,
        default: () => ({}),
    },
    selectedStops: {
        type: Array,
        default: () => [],
    },
    selectedDate: {
        type: Date,
        default: () => new Date()
    },
});

const emit = defineEmits(['selectStop']);

const lineSource = 'line-source';
const lineLayer = 'line-layer';
const stopsSource = 'stops-source';
const stopsLayer = 'stops-layer';

const mapContainer = ref(null);
const { map, createMap, setUserPosition, onFeatureClick, setGeoJSONSource } = useMap();
const userPosition = ref(null);
const selectedService = ref(null);
const selectedRoute = ref({
    infos: null,
    direction: null,
    trace: null,
    geoJsonLine: null,
    geoJsonLineStyle: null,
    stops: null,
    geoJsonStops: null,
});

const stopTooltipNode = document.createElement('div');
stopTooltipNode.className = 'tooltip-text';
const stopTooltip = new maplibregl.Popup({
    offset: 10,
    closeButton: false,
    closeOnClick: false,
}).setDOMContent(stopTooltipNode);
let hoveredStop = null;
let isMapLoaded = false;

const { getServiceType, fetchLine, fetchStops } = useSchedulesStore();

const { t } = useI18n({
    messages: {
        fr: {
            recenter: 'Voir tout le tracé',
            user_position: 'Utiliser ma position',
            map_description: 'Carte interactive du parcours',
        },
        en: {
            recenter: 'See the whole path',
            user_position: 'Use my position',
            map_description: 'Interactive route map',
        },
    },
});

function initializeMap() {
    createMap(mapContainer.value, {
        center: [-71.207, 46.8],
        zoom: 11,
    });
    map.value.on('load', () => {
        isMapLoaded = true;
        onMapReady();
    });

    onFeatureClick([stopsLayer], (feature) => {
        emit('selectStop', feature.properties.noArret);
    });
    map.value.on('mousemove', stopsLayer, (e) => {
        const [feature] = e.features;
        if (feature.properties.noArret === hoveredStop) return;

        hoveredStop = feature.properties.noArret;
        stopTooltipNode.textContent = `${feature.properties.noArret} - ${feature.properties.nom}`;
        stopTooltip.setLngLat(feature.geometry.coordinates).addTo(map.value);
    });
    map.value.on('mouseleave', stopsLayer, () => {
        hoveredStop = null;
        stopTooltip.remove();
    });
}

function onMapReady() {
    const buttons = map.value.getContainer().querySelectorAll('a, button');
    Array.from(buttons).forEach((e) => e.setAttribute('tabindex', -1));
    if (props.route) {
        selectRoute(props.route);
    }
}

function renderUserPosition() {
    if (!map.value || !userPosition.value) return;

    const { latitude, longitude } = userPosition.value.coords;
    setUserPosition([longitude, latitude]);
}

function renderLine() {
    if (!isMapLoaded || !selectedRoute.value.geoJsonLine) {
        return;
    }

    if (map.value.getLayer(lineLayer)) {
        map.value.removeLayer(lineLayer);
    }
    if (map.value.getSource(lineSource)) {
        map.value.removeSource(lineSource);
    }
    map.value.addSource(lineSource, {
        type: 'geojson',
        data: selectedRoute.value.geoJsonLine,
    });
    map.value.addLayer({
        id: lineLayer,
        type: 'line',
        source: lineSource,
        layout: {
            'line-join': 'round',
            'line-cap': 'round',
        },
        paint: {
            'line-color': selectedRoute.value.geoJsonLineStyle?.color || '#007bff',
            'line-width': selectedRoute.value.geoJsonLineStyle?.weight || 5.5,
            'line-opacity': selectedRoute.value.geoJsonLineStyle?.opacity || 1,
        },
    }, map.value.getLayer(stopsLayer) ? stopsLayer : undefined); // Keeps the stops above the line.
}

function getStopsGeoJSON() {
    return {
        ...selectedRoute.value.geoJsonStops,
        features: selectedRoute.value.geoJsonStops.features.map((feature) => ({
            ...feature,
            properties: {
                ...feature.properties,
                selected: props.selectedStops.includes(feature.properties.noArret.toString()),
            },
        })),
    };
}

function renderStops() {
    if (!isMapLoaded || !selectedRoute.value.geoJsonStops) {
        return;
    }

    setGeoJSONSource(stopsSource, getStopsGeoJSON());

    if (map.value.getLayer(stopsLayer)) {
        map.value.setPaintProperty(stopsLayer, 'circle-color', getStopsColor());
        return;
    }

    map.value.addLayer({
        id: stopsLayer,
        type: 'circle',
        source: stopsSource,
        paint: {
            'circle-color': getStopsColor(),
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 12, 4, 16, 6],
            'circle-stroke-width': 2,
            'circle-stroke-color': '#fff',
        },
    });
}

function getStopsColor() {
    return ['case', ['get', 'selected'], '#0070ee', selectedService.value?.color || '#007bff'];
}

function locateUser() {
    const position = new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
    });
    position
        .then((position) => {
            userPosition.value = position;
            renderUserPosition();
            recenterMap('user');
        })
        .catch(() => {});
}

async function recenterMap(position) {
    await nextTick();
    if (!map.value) return;

    if (position === 'user' && userPosition.value) {
        const { latitude, longitude } = userPosition.value.coords;
        map.value.flyTo({
            center: [longitude, latitude],
            zoom: 13
        });
    } else if (position === 'route' && selectedRoute.value.geoJsonLine) {
        const bounds = new maplibregl.LngLatBounds();
        if (selectedRoute.value.geoJsonLine.coordinates) {
            selectedRoute.value.geoJsonLine.coordinates.forEach(line => {
                if (Array.isArray(line[0])) {
                    line.forEach(coord => bounds.extend(coord));
                } else {
                    bounds.extend(line);
                }
            });
        }
        if (!bounds.isEmpty()) {
            map.value.fitBounds(bounds, { padding: 15 });
        }
    }
}

async function selectRoute(route) {
    if (!route || !props.direction?.code) return;

    const direction = props.direction.code;
    selectedService.value = getServiceType(route.codeTypeService);
    try {
        const lines = await fetchLine(
            route.noParcours,
            direction,
            'all',
            props.selectedDate,
        );
        selectedRoute.value.infos = route;
        selectedRoute.value.direction = direction;
        selectedRoute.value.line = lines[0];
        selectedRoute.value.geoJsonLine = toGeoJSON({
            type: 'MultiLineString',
            coordinates: lines.map((t) => t.polyligne),
        });
        selectedRoute.value.geoJsonLineStyle = {
            color: selectedService.value?.color || '#007bff',
            weight: 5.5,
            opacity: 1,
        };
        selectedRoute.value.stops = await fetchStops(
            route.noParcours,
            direction,
            props.selectedDate,
        );
        selectedRoute.value.geoJsonStops = GeoJSON.parse(selectedRoute.value.stops, {
            Point: ['latitude', 'longitude'],
        });
        renderLine();
        renderStops();
        recenterMap('route');
    } catch (error) {
        console.error('Error loading route data:', error);
    }
}

watch(
    () => props.selectedDate,
    () => {
        if (props.route) {
            selectRoute(props.route);
        }
    }
);

watch(
    () => props.route,
    (newRoute) => {
        if (newRoute) {
            selectRoute(newRoute);
        }
    }
);

watch(
    () => props.selectedStops.join(),
    () => {
        renderStops();
    }
);

onMounted(initializeMap);
</script>

<style lang="scss">
@import "vue/map";

.maplibregl-ctrl-attrib-inner {
    @include rem-typo(8px);
}
.maplibregl-popup-content {
    padding: 0;
}
.tooltip-text {
    @include rem-typo(18px);
    color: $color-blue-60;
    padding: 12px 16px;
    font-family: $body-font-family;
}
</style>

<style lang="scss" scoped>
.map-container {
    position: relative;
    margin-top: 16px;
    border-radius: 15px;
    overflow: hidden;
    @include breakpoint(large) {
        border-radius: 0;
        margin: 0;
    }
}
.map {
    height: 100%;
}
.geo-button {
    position: absolute;
    right: 10px;
    bottom: 50px;
    width: 45px;
    height: 45px;
    border-radius: 50%;
    background-color: $color-white;
    box-shadow: $floating-shadow;
    transition: background-color $default-transition;
    border: 0;
    &:hover {
        background-color: $color-blue-60;
        svg {
            fill: $color-white;
        }
    }
    svg {
        fill: $color-blue-60;
        transition: fill $default-transition;
    }
}
.recenter-button {
    position: absolute;
    right: 10px;
    bottom: 110px;
    box-shadow: $floating-shadow;
}
@keyframes geoButtonPulsateIcon {
    from {
        fill: $color-blue-60;
    }
    50% {
        fill: $color-white;
    }
    to {
        fill: $color-blue-60;
    }
}
</style>
